/**
 * @fileoverview Venues Redux slice.
 * @summary Manages venues state (list, CRUD operations).
 *
 * @package KaraoNow
 * @module apps/front/src/store/slices/venuesSlice
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'

/**
 * @summary Venue interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface Venue {
  id: string
  name: string
  address?: string
  city: string
  country?: string
  googleMapsUrl?: string
  createdAt?: string | Date
  updatedAt?: string | Date
}

/**
 * @summary Venues state interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface VenuesState {
  venues: Venue[]
  isLoading: boolean
  error: string | null
}

/**
 * @summary Initial venues state.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const initialState: VenuesState = {
  venues: [],
  isLoading: false,
  error: null,
}

/**
 * @summary Fetch all venues.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const fetchVenues = createAsyncThunk('venues/fetchVenues', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest<{ venues: Venue[] }>({
      method: 'GET',
      url: API_ENDPOINTS.VENUES.LIST,
    })
    if (!response.success) {
      return rejectWithValue(response.message || 'Failed to fetch venues')
    }
    return response.data!.venues
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch venues')
  }
})

/**
 * @summary Create a new venue.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const createVenue = createAsyncThunk(
  'venues/createVenue',
  async (
    venueData: { name: string; address?: string; city: string; country?: string; googleMapsUrl?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiRequest<{ venue: Venue }>({
        method: 'POST',
        url: API_ENDPOINTS.VENUES.CREATE,
        data: venueData,
      })
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to create venue')
      }
      return response.data!.venue
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create venue')
    }
  },
)

/**
 * @summary Update an existing venue.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const updateVenue = createAsyncThunk(
  'venues/updateVenue',
  async (
    { venueId, venueData }: { venueId: string; venueData: Partial<Omit<Venue, 'id'>> },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiRequest<{ venue: Venue }>({
        method: 'PUT',
        url: API_ENDPOINTS.VENUES.UPDATE(venueId),
        data: venueData,
      })
      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update venue')
      }
      return response.data!.venue
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update venue')
    }
  },
)

/**
 * @summary Delete a venue.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const deleteVenue = createAsyncThunk('venues/deleteVenue', async (venueId: string, { rejectWithValue }) => {
  try {
    const response = await apiRequest({
      method: 'DELETE',
      url: API_ENDPOINTS.VENUES.DELETE(venueId),
    })
    if (!response.success) {
      return rejectWithValue(response.message || 'Failed to delete venue')
    }
    return venueId
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete venue')
  }
})

/**
 * @summary Venues slice.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const venuesSlice = createSlice({
  name: 'venues',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVenues.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchVenues.fulfilled, (state, action) => {
        state.isLoading = false
        state.venues = action.payload
      })
      .addCase(fetchVenues.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(createVenue.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createVenue.fulfilled, (state, action) => {
        state.isLoading = false
        state.venues.push(action.payload)
      })
      .addCase(createVenue.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(updateVenue.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateVenue.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.venues.findIndex((v) => v.id === action.payload.id)
        if (index !== -1) {
          state.venues[index] = action.payload
        }
      })
      .addCase(updateVenue.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(deleteVenue.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteVenue.fulfilled, (state, action) => {
        state.isLoading = false
        state.venues = state.venues.filter((v) => v.id !== action.payload)
      })
      .addCase(deleteVenue.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default venuesSlice.reducer

